import React, { useContext, useEffect } from "react";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { map as mapFp } from "lodash/fp";
import { find } from "lodash";
import { ResourceMetaCreate, ResourceCategory, Attachment, EquipmentTypeEnum, FuelTypeEnum, ResourceMetaStatusTypeEnum } from "@/graphql/generated/graphql";
import useFileUploader from "@/hooks/useFileUploader";
import { MAX_ATTACHMENTS_ALLOWED } from "@/utils/constants";
import { titleizeCamelCase, arrayLength } from "@/utils/helperFunctions";
import ImageSection from "@/components/ImageSection";
import type { ResourceMetaChangeHandler } from "./types";
import CurrentUserContext from "@/Contexts/CurrentUser";

const mapSignedBlobId = mapFp("signedBlobId");

type EquipmentResourceFieldsProps = {
  isEditing: boolean;
  resourceMeta: ResourceMetaCreate;
  equipmentType?: string;
  onChange: ResourceMetaChangeHandler;
  isVehicle: boolean;
  resourceCategories: ResourceCategory[];
  attachments?: Attachment[] | undefined;
};

const EquipmentResourceFields: React.FC<EquipmentResourceFieldsProps> = ({
  isEditing,
  resourceMeta,
  equipmentType,
  onChange,
  resourceCategories,
  isVehicle,
  attachments
}) => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { isUploading, files, handleUploadImage, handleRemoveImage } = useFileUploader();

  useEffect(() => {
    onChange("signedIds", mapSignedBlobId(files))
  }, [files]);

  return (
    <>
      <Grid container size={12} p={2}>
        {resourceCategories.length > 0 && (
          <Autocomplete
            id="category-selector"
            options={resourceCategories}
            fullWidth
            autoHighlight
            value={find(resourceCategories, category => category.id === resourceMeta.resourceCategoryId)}
            onChange={(_, newValue) => onChange("resourceCategoryId", newValue?.id)}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Resource Categories"
                slotProps={{
                  htmlInput: {
                    ...params.inputProps,
                    autoComplete: "new-password",
                  },
                }}
              />
            )}
          />
        )}
      </Grid>
      <Grid container size={12} p={2}>
        <ToggleButtonGroup
          color="primary"
          value={resourceMeta?.equipmentType || undefined}
          exclusive
          onChange={(_, newValue: EquipmentTypeEnum | null) => {
            if (newValue !== null) {
              onChange("equipmentType", newValue);
            }
          }}
          aria-label="Equipment Type"
          disabled={!!equipmentType}
        >
          <ToggleButton value={EquipmentTypeEnum.Internal}>Internal</ToggleButton>
          <ToggleButton value={EquipmentTypeEnum.External}>External</ToggleButton>
        </ToggleButtonGroup>
      </Grid>
      <Grid container size={12} p={2}>
        <TextField
          autoComplete="equipment-short-code"
          required
          value={resourceMeta?.shortCode || ""}
          fullWidth
          id="equipment-short-code"
          label="Short Code"
          name="equipment-short-code"
          onChange={(e) => {
            onChange("shortCode", e.target.value);
          }}
        />
      </Grid>
      {isVehicle ? (
        <>
          <Grid container size={12} p={2}>
            <TextField
              autoComplete="equipment-serial-number"
              value={resourceMeta?.serialNumber || ""}
              fullWidth
              id="equipment-serial-number"
              label="VIN/Serial Number"
              name="equipment-serial-number"
              onChange={(e) => {
                onChange("serialNumber", e.target.value);
              }}
            />
          </Grid>
          <Grid container size={12} p={2}>
            <TextField
              autoComplete="equipment-make-model"
              value={resourceMeta?.makeModel || ""}
              fullWidth
              id="equipment-make-model"
              label="Make and Model"
              name="equipment-make-model"
              onChange={(e) => {
                onChange("makeModel", e.target.value);
              }}
            />
          </Grid>
          <Grid container spacing={1} size={12} p={2}>
            <Grid size={6} >
              <TextField
                autoComplete="equipment-capacity"
                value={resourceMeta?.capacity || ""}
                fullWidth
                id="equipment-capacity"
                label="Capacity"
                name="equipment-capacity"
                onChange={(e) => {
                  onChange("capacity", e.target.value);
                }}
              />
            </Grid>
            <Grid size={6} >
              <TextField
                autoComplete="equipment-max-reach"
                value={resourceMeta?.maxReach || ""}
                fullWidth
                id="equipment-max-reach"
                label="Max Reach"
                name="equipment-max-reach"
                onChange={(e) => {
                  onChange("maxReach", e.target.value);
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={1} size={12} p={2}>
            <Grid size={6} >
              <TextField
                autoComplete="equipment-weight-dimensions"
                value={resourceMeta?.weightDimensions || ""}
                fullWidth
                id="equipment-weight-dimensions"
                label="Weight/Dimensions"
                name="equipment-weight-dimensions"
                onChange={(e) => {
                  onChange("weightDimensions", e.target.value);
                }}
              />
            </Grid>
            <Grid size={6} >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Next Service Date"
                  value={resourceMeta?.nextServiceDate ? dayjs(resourceMeta.nextServiceDate, "DD-MM-YYYY") : null}
                  format={dateTimePreference.date}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      onChange("nextServiceDate", newValue.format("DD-MM-YYYY"));
                    } else if (newValue === null) {
                      onChange("nextServiceDate", null);
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid container spacing={1} size={12} p={2}>
            <Grid size={6} >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Insurance Expiry Date"
                  value={resourceMeta?.insuranceExpiryDate ? dayjs(resourceMeta.insuranceExpiryDate, "DD-MM-YYYY") : null}
                  format={dateTimePreference.date}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      onChange("insuranceExpiryDate", newValue.format("DD-MM-YYYY"));
                    } else if (newValue === null) {
                      onChange("insuranceExpiryDate", null);
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid size={6} >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Permit Expiry Date"
                  value={resourceMeta?.permitExpiryDate ? dayjs(resourceMeta.permitExpiryDate, "DD-MM-YYYY") : null}
                  format={dateTimePreference.date}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      onChange("permitExpiryDate", newValue.format("DD-MM-YYYY"));
                    } else if (newValue === null) {
                      onChange("permitExpiryDate", null);
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
          <Grid container size={12} p={2}>
            <TextField
              autoComplete="operator-requirement"
              value={resourceMeta?.operatorRequirements || ""}
              fullWidth
              id="equipment-operator-requirements"
              label="Operator Requirements"
              name="equipment-operator-requirements"
              onChange={(e) => {
                onChange("operatorRequirements", e.target.value);
              }}
            />
          </Grid>
          <Grid container spacing={1} size={12} p={2}>
            <Grid size={6}>
              <Autocomplete
                fullWidth
                disableClearable
                options={Object.values(FuelTypeEnum)}
                getOptionLabel={(option) => titleizeCamelCase(option)}
                value={resourceMeta?.fuelType || FuelTypeEnum.Diesel}
                onChange={(_, value) => onChange("fuelType", value)}
                renderInput={(params) => (
                  <TextField {...params} label="Fuel Type" fullWidth />
                )}
              />

            </Grid>
            <Grid size={6}>
              <Autocomplete
                fullWidth
                disableClearable
                options={Object.values(ResourceMetaStatusTypeEnum)}
                getOptionLabel={(option) => titleizeCamelCase(option)}
                value={resourceMeta?.status || ResourceMetaStatusTypeEnum.Available}
                onChange={(_, value) => onChange("status", value)}
                renderInput={(params) => (
                  <TextField {...params} label="Status" fullWidth />
                )}
              />
            </Grid>
          </Grid>
          <Grid size={12} p={2}>
            <ImageSection
              attachments={attachments || files}
              handleRemoveImage={handleRemoveImage}
              disableAttaching={isEditing || arrayLength(files) >= MAX_ATTACHMENTS_ALLOWED || arrayLength(attachments) > 0 || isUploading}
              handleUploadImage={(e) => handleUploadImage(e, "CompanyResource")}
              isUploading={isUploading}
              hasImages={(arrayLength(files) > 0 || arrayLength(attachments) > 0)}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid container size={12} p={2}>
            <TextField
              autoComplete="equipment-unit"
              value={resourceMeta?.unit || ""}
              fullWidth
              id="equipment-unit"
              label="Unit"
              name="equipment-unit"
              onChange={(e) => {
                onChange("unit", e.target.value);
              }}
            />
          </Grid>
          <Grid container size={12} p={2}>
            <TextField
              autoComplete="equipment-quantity"
              value={resourceMeta?.quantity}
              fullWidth
              id="equipment-quantity"
              label="Quantity"
              name="equipment-quantity"
              onChange={(e) => {
                onChange("quantity", e.target.value);
              }}
            />
          </Grid>
          <Grid container spacing={1} size={12} p={2}>
            <Grid size={6}>
              <TextField
                autoComplete="batch-number"
                value={resourceMeta?.batchNumber || ""}
                fullWidth
                id="batch-number"
                label="Batch Number"
                name="batch-number"
                onChange={(e) => {
                  onChange("batchNumber", e.target.value);
                }}
              />
            </Grid>
            <Grid size={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Expiry Date"
                  value={resourceMeta?.expiryDate ? dayjs(resourceMeta.expiryDate, "DD-MM-YYYY") : null}
                  format={dateTimePreference.date}
                  onChange={(newValue) => {
                    if (newValue && newValue.isValid()) {
                      onChange("expiryDate", newValue.format("DD-MM-YYYY"));
                    } else if (newValue === null) {
                      onChange("expiryDate", null);
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </>
      )}
      <Grid container size={12} p={2}>
        <FormControlLabel
          label="Scheduleable"
          control={
            <Checkbox
              onChange={(_, checked) => onChange("isScheduleable", checked)}
              checked={resourceMeta?.isScheduleable || false}
              inputProps={{ "aria-label": "controlled" }}
            />
          }
        />
      </Grid>
    </>
  );
};

export default EquipmentResourceFields;
